import { useMutation } from '@apollo/client'
import { type MutationBaseOptions } from '@apollo/client/core/watchQueryOptions'
import { Autocomplete, TextField } from '@mui/material'
import { type GridCellEditingApi } from '@mui/x-data-grid/models/api/gridEditingApi'
import { SystemStatus } from '__generated__/graphql'
import startCase from 'lodash.startcase'
import { type FC } from 'react'
import { UpdateSystemStatus } from 'recruiter/graphql/updateSystemStatus.gql'
import useToast from 'shared/components/Toast/useToast'
import { formatGraphQLError } from 'shared/utils'

interface SystemStatusEditCellProps {
    /** A reference to the MUI DataGrid cell editing API. Allows this component to manipulate the DataGrid it lives in. */
    api: GridCellEditingApi
    /** The candidate's active system status to display. */
    currentSystemStatus?: SystemStatus | null
    /** An array (or a function that returns an array) that specifies which queries you want to refetch after this cell is successfully edited.  */
    refetchQueries?: MutationBaseOptions['refetchQueries']
    /** The roster ID of the candidate whose system status is being edited. */
    candidateId: string
    /** The ID of the row in the DataGrid that this cell belongs to. */
    rowId: string
}

const SystemStatusEditCell: FC<SystemStatusEditCellProps> = ({
    api,
    candidateId,
    refetchQueries,
    currentSystemStatus = SystemStatus.NoStatus,
    rowId = candidateId,
}) => {
    const toast = useToast()

    const [updateSystemStatus] = useMutation(UpdateSystemStatus, {
        refetchQueries,
        onCompleted(data) {
            if (data.updateCandidateSystemStatus.success) {
                toast({
                    title: 'Success',
                    message:
                        data.updateCandidateSystemStatus.message ||
                        'Candidate registration status updated successfully',
                    severity: 'success',
                })

                api.stopCellEditMode({
                    field: 'systemStatus',
                    id: rowId,
                })
            } else {
                toast({
                    title: 'Error',
                    message:
                        data.updateCandidateSystemStatus.message ||
                        'Error updating registration status',
                    severity: 'error',
                })
            }
        },
        onError(error) {
            toast({
                title: 'Error',
                message: formatGraphQLError(
                    error,
                    'Error updating registration status'
                ),
                severity: 'error',
            })
        },
    })

    return (
        <Autocomplete
            disableClearable
            fullWidth
            sx={{
                '& .MuiFormControl-root': {
                    height: '100%',
                },
                '& .MuiInputBase-root': {
                    height: '100%',
                    borderRadius: '0px',
                },
            }}
            renderInput={(props) => (
                <TextField
                    {...props}
                    fullWidth
                    inputProps={{
                        ...props.inputProps,
                        height: '100%',
                    }}
                />
            )}
            onChange={(event, systemStatus) => {
                updateSystemStatus({
                    variables: {
                        candidateIds: [candidateId],
                        systemStatus,
                    },
                })
            }}
            value={currentSystemStatus ?? SystemStatus.NoStatus}
            options={Object.values(SystemStatus).filter(
                (status) => status !== SystemStatus.Null
            )}
            getOptionLabel={(option) =>
                startCase(option || SystemStatus.NoStatus)
            }
        />
    )
}

export default SystemStatusEditCell
